import { validateMove, fromSerialized, scoreWord, type Dictionary } from '@scrabble-daily/engine';
import type { SerializedBoardDto } from '../api/puzzles';
import type { StagedPlacement } from './store';

export interface WordCheck {
  word: string;
  score: number;
  valid: boolean;
  /** Every cell of the word — staged and pre-existing — for board highlighting. */
  cells: Array<{ row: number; col: number }>;
}

export interface PreviewResult {
  /** Structurally legal AND every formed word is in the dictionary. */
  valid: boolean;
  /** Total move score (cross-words + bingo included). */
  score: number;
  bingo: boolean;
  /** Primary word first, then cross-words, each checked independently. */
  words: WordCheck[];
  /** Structural problem (gap, off-axis, …) when the words can't even form. */
  message: string | null;
}

const EMPTY: PreviewResult = { valid: false, score: 0, bingo: false, words: [], message: null };

const FRIENDLY_ERRORS: Record<string, string> = {
  NO_PLACEMENTS: 'Place some tiles to see a score',
  OUT_OF_BOUNDS: 'A tile is off the board',
  CELL_OCCUPIED: 'That square is already taken',
  OFF_AXIS: 'Tiles must sit in a single row or column',
  GAP_IN_PLACEMENTS: 'No gaps — your tiles must form one continuous word',
  NOT_COVERING_STAR_FIRST_MOVE: 'The first word must cover the centre star',
  DISCONNECTED: 'Your word must connect to tiles already on the board',
  SINGLE_TILE_NOT_FORMING_WORD: 'That placement does not form a word',
  BLANK_MISSING_LETTER: 'Choose a letter for your blank tile',
};

/**
 * Live word check (spec 10.4, extended): run the shared engine structurally
 * (dictionary bypassed) to extract the primary word and every cross-word,
 * then validate each word against the dictionary individually so the UI can
 * highlight exactly which words are good and which aren't.
 */
export function previewMove(
  board: SerializedBoardDto,
  staged: StagedPlacement[],
  dict: Dictionary,
): PreviewResult {
  if (staged.length === 0) return EMPTY;
  if (staged.some((p) => p.is_blank && !/^[A-Z]$/.test(p.letter))) {
    return { ...EMPTY, message: FRIENDLY_ERRORS.BLANK_MISSING_LETTER! };
  }

  // Structural pass with an always-yes dictionary: shape errors (gaps,
  // off-axis, disconnection) surface here; word legality is checked below.
  const result = validateMove(fromSerialized(board), staged, {
    isInDictionary: () => true,
  });

  if (!result.ok) {
    return { ...EMPTY, message: FRIENDLY_ERRORS[result.error_code] ?? result.message };
  }

  const words: WordCheck[] = [result.primary_word, ...result.cross_words].map((w) => ({
    word: w.word,
    score: scoreWord(w),
    valid: dict.isInDictionary(w.word),
    cells: w.cells.map((c) => ({ row: c.row, col: c.col })),
  }));

  return {
    valid: words.every((w) => w.valid),
    score: result.move_score,
    bingo: result.bingo,
    words,
    message: null,
  };
}
